import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const useFeedback = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const createFeedback = useCallback(
    async ({ rating, viewKey, bookingNumber, text }) => {
      try {
        return await sendRequest({
          url: '/api/v1/feedback',
          method: 'POST',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { rating, viewKey, bookingNumber, text },
        });
      } catch (err) {
        toast.error('Fehler beim Senden des Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getMyFeedback = useCallback(
    async ({ includeSolved = true, markAsRead = false } = {}) => {
      try {
        return await sendRequest({
          url: '/api/v1/feedback/me',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { includeSolved, markAsRead },
        });
      } catch (err) {
        toast.error('Fehler beim Laden deines Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateMyFeedback = useCallback(
    async ({ feedbackId, rating, viewKey, bookingNumber, text }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/me/${feedbackId}`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { rating, viewKey, bookingNumber, text },
        });
      } catch (err) {
        toast.error('Fehler beim Bearbeiten deines Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const deleteMyFeedback = useCallback(
    async feedbackId => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/me/${feedbackId}`,
          method: 'DELETE',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Loeschen deines Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const replyToMyFeedback = useCallback(
    async ({ feedbackId, text }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/me/${feedbackId}/reply`,
          method: 'POST',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { text },
        });
      } catch (err) {
        toast.error('Fehler beim Senden der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getAllFeedback = useCallback(
    async ({ includeSolved = true } = {}) => {
      try {
        return await sendRequest({
          url: '/api/v1/feedback',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { includeSolved },
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const setSolved = useCallback(
    async ({ feedbackId, solved, response }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/solved`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { solved, response },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Feedback-Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const setResponse = useCallback(
    async ({ feedbackId, response }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/response`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { response },
        });
      } catch (err) {
        toast.error('Fehler beim Speichern der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const addResponseMessage = useCallback(
    async ({ feedbackId, text }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/reply`,
          method: 'POST',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { text },
        });
      } catch (err) {
        toast.error('Fehler beim Senden der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const setRejected = useCallback(
    async ({ feedbackId, rejected }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/rejected`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { rejected },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Feedback-Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const setInProgress = useCallback(
    async ({ feedbackId, inProgress }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/inProgress`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { inProgress },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Feedback-Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    createFeedback,
    getMyFeedback,
    updateMyFeedback,
    deleteMyFeedback,
    replyToMyFeedback,
    getAllFeedback,
    setSolved,
    setResponse,
    addResponseMessage,
    setRejected,
    setInProgress,
  };
};

export default useFeedback;

